/**
 * Chat Window Component
 * Main chat interface with messages and input
 */

import type { Message } from '../types';

export class ChatWindow {
  private container: HTMLElement;
  private onSendMessage: (content: string) => Promise<void>;
  private onTyping: (isTyping: boolean) => void;
  private messages: Message[] = [];
  private typingTimeout: number | null = null;

  constructor(
    container: HTMLElement,
    _conversationId: number,
    onSendMessage: (content: string) => Promise<void>,
    onTyping: (isTyping: boolean) => void
  ) {
    this.container = container;
    this.onSendMessage = onSendMessage;
    this.onTyping = onTyping;
    this.render();
  }

  private render() {
    this.container.innerHTML = `
      <div class="chat-header" id="chat-header">
        <div class="agent-status" id="agent-status">
          <div class="status-indicator offline" id="status-indicator"></div>
          <span class="agent-name" id="agent-name">Support Team</span>
        </div>
      </div>
      <div class="messages-container" id="messages-container"></div>
      <div class="typing-indicator hidden" id="typing-indicator">
        <div class="typing-dots">
          <div class="typing-dot"></div>
          <div class="typing-dot"></div>
          <div class="typing-dot"></div>
        </div>
        <span id="typing-text">Agent is typing...</span>
      </div>
      <div class="message-input-container">
        <div class="message-input-wrapper">
          <textarea 
            id="message-input" 
            class="message-input" 
            placeholder="Type your message..."
            rows="1"
            maxlength="5000"
          ></textarea>
          <button id="send-button" class="send-button" disabled>
            ➤
          </button>
        </div>
      </div>
    `;

    this.attachEventListeners();
  }

  private attachEventListeners() {
    const input = this.container.querySelector('#message-input') as HTMLTextAreaElement;
    const sendBtn = this.container.querySelector('#send-button') as HTMLButtonElement;

    // Auto-resize textarea
    input?.addEventListener('input', () => {
      input.style.height = 'auto';
      input.style.height = Math.min(input.scrollHeight, 100) + 'px';
      
      // Enable/disable send button
      sendBtn.disabled = !input.value.trim();

      // Send typing indicator (debounced)
      this.handleTyping();
    });

    // Send on Enter (Shift+Enter for new line)
    input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    });

    sendBtn?.addEventListener('click', () => {
      this.sendMessage();
    });
  }

  private handleTyping() {
    // Clear existing timeout
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    // Notify typing started
    this.onTyping(true);

    // Stop typing after 2 seconds of inactivity
    this.typingTimeout = window.setTimeout(() => {
      this.onTyping(false);
    }, 2000);
  }

  private async sendMessage() {
    const input = this.container.querySelector('#message-input') as HTMLTextAreaElement;
    const content = input?.value.trim();

    if (!content) return;

    // Clear input
    input.value = '';
    input.style.height = 'auto';
    const sendBtn = this.container.querySelector('#send-button') as HTMLButtonElement;
    sendBtn.disabled = true;

    // Stop typing indicator
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
    this.onTyping(false);

    try {
      await this.onSendMessage(content);
    } catch (error) {
      console.error('[ChatWindow] Send failed:', error);
      // Restore message to input on error
      input.value = content;
      sendBtn.disabled = false;
    }
  }

  addMessage(message: Message) {
    this.messages.push(message);
    const container = this.container.querySelector('#messages-container');
    if (!container) return;

    const messageEl = this.createMessageElement(message);
    container.appendChild(messageEl);
    
    // Scroll to bottom
    container.scrollTop = container.scrollHeight;
  }

  setMessages(messages: Message[]) {
    this.messages = messages;
    this.renderMessages();
  }

  setAgentTyping(isTyping: boolean, agentName?: string) {
    const indicator = this.container.querySelector('#typing-indicator');
    const typingText = this.container.querySelector('#typing-text');
    
    if (indicator) {
      indicator.classList.toggle('hidden', !isTyping);
      
      // Update typing text with agent name if available
      if (typingText && agentName) {
        typingText.textContent = `${agentName} is typing...`;
      } else if (typingText) {
        typingText.textContent = 'Agent is typing...';
      }
      
      if (isTyping) {
        // Scroll to show typing indicator
        const container = this.container.querySelector('#messages-container');
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }
    }
  }

  setAgentInfo(agentName: string | null, isOnline: boolean) {
    const agentNameEl = this.container.querySelector('#agent-name');
    const statusIndicator = this.container.querySelector('#status-indicator');
    
    if (agentNameEl) {
      agentNameEl.textContent = agentName || 'Support Team';
    }
    
    if (statusIndicator) {
      statusIndicator.classList.toggle('online', isOnline);
      statusIndicator.classList.toggle('offline', !isOnline);
    }
  }

  private renderMessages() {
    const container = this.container.querySelector('#messages-container');
    if (!container) return;

    container.innerHTML = '';
    this.messages.forEach(msg => {
      const msgEl = this.createMessageElement(msg);
      container.appendChild(msgEl);
    });

    // Scroll to bottom
    container.scrollTop = container.scrollHeight;
  }

  private createMessageElement(message: Message): HTMLElement {
    const div = document.createElement('div');
    div.className = `message ${message.direction}`;
    
    const time = new Date(message.timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    // From visitor's perspective:
    // - inbound = visitor's own messages (show status icons)
    // - outbound = agent's messages (show sender name)
    const senderName = message.direction === 'outbound' && message.sender_name 
      ? `<span>${message.sender_name}</span>` 
      : '';

    div.innerHTML = `
      <div class="message-bubble">${this.escapeHtml(message.content)}</div>
      <div class="message-meta">
        ${senderName}
        <span>${time}</span>
        ${message.direction === 'inbound' ? this.getStatusIcon(message.status) : ''}
      </div>
    `;

    return div;
  }

  private getStatusIcon(status: string): string {
    switch (status) {
      case 'sent':
        return '✓';
      case 'delivered':
      case 'read':
        return '✓✓';
      default:
        return '';
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  destroy() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
    this.container.innerHTML = '';
  }
}
